import { ref } from 'vue';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export function useCurriculumDesigner() {
  const messages = ref<ChatMessage[]>([]);
  const isStreaming = ref(false);
  const createdCurriculumId = ref<string | null>(null);
  const error = ref<string | null>(null);
  const sessionId = ref<string | null>(null);
  const isLoadingSession = ref(false);

  async function createSession(): Promise<string> {
    const response = await fetch(`${API_URL}/api/chat/sessions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ type: 'curriculum_designer' }),
    });

    if (!response.ok) {
      throw new Error(response.status === 401 ? 'Not authenticated' : `HTTP ${response.status}`);
    }

    const data = await response.json();
    return data.id;
  }

  async function loadSession(id: string): Promise<void> {
    isLoadingSession.value = true;
    try {
      const response = await fetch(`${API_URL}/api/chat/sessions/${id}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 404) return; // Session not found, start fresh
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      sessionId.value = id;
      messages.value = data.messages.map((m: { role: string; content: string }) => ({
        role: m.role as 'user' | 'assistant',
        content: m.content,
      }));
      if (data.curriculumId) {
        createdCurriculumId.value = data.curriculumId;
      }
    } finally {
      isLoadingSession.value = false;
    }
  }

  async function ensureSession(): Promise<string> {
    if (sessionId.value) return sessionId.value;
    const id = await createSession();
    sessionId.value = id;
    return id;
  }

  async function sendMessage(userMessage: string) {
    if (!userMessage.trim() || isStreaming.value) return;

    error.value = null;

    try {
      const sid = await ensureSession();

      messages.value.push({ role: 'user', content: userMessage });
      isStreaming.value = true;

      // Placeholder for the streaming assistant response
      const assistantMsg: ChatMessage = { role: 'assistant', content: '', isStreaming: true };
      messages.value.push(assistantMsg);

      // Build messages for API (exclude the empty assistant placeholder)
      const apiMessages = messages.value
        .slice(0, -1)
        .map((m) => ({ role: m.role, content: m.content }));

      const response = await fetch(`${API_URL}/api/chat/curriculum-designer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ sessionId: sid, messages: apiMessages }),
      });

      if (!response.ok) {
        throw new Error(response.status === 401 ? 'Not authenticated' : `HTTP ${response.status}`);
      }

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      let done = false;
      while (!done) {
        const result = await reader.read();
        if (result.done) {
          done = true;
          break;
        }
        const { value } = result;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let currentEvent = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7);
          } else if (line.startsWith('data: ') && currentEvent) {
            const data = JSON.parse(line.slice(6));
            switch (currentEvent) {
              case 'text_delta':
                assistantMsg.content += data.text;
                break;
              case 'curriculum_created':
                createdCurriculumId.value = data.curriculumId;
                break;
              case 'error':
                error.value = data.message;
                break;
            }
            currentEvent = '';
          }
        }
      }

      assistantMsg.isStreaming = false;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Connection failed';
    } finally {
      isStreaming.value = false;
    }
  }

  return {
    messages,
    isStreaming,
    isLoadingSession,
    createdCurriculumId,
    error,
    sessionId,
    sendMessage,
    loadSession,
  };
}
